import { createPlacement } from '@post-office/placement-common-v2';
import { type EligibleMessage } from '@post-office/shared-contracts/shared-request-middleware-context';
import { usePlacementApi } from '@post-office/use-placement-api';

import type { MessageTemplateContext } from './test-messages/test-message';
import { useEffect, useState } from 'react';
import FeatureGates from '@atlaskit/feature-gate-js-client';

type PlacementProps = {
	/**
	 * Whether the content (page, space, blog etc) has restrictions
	 */
	isContentRestricted: boolean;
};

export const { createFrontend, createBackend } = createPlacement({
	placementId: 'share-and-restrict-button',
	options: {
		choreographer: {
			isDefaultEnabled: true,
		},
		hideInIsolatedCloud: false,
	},
});

export const PlacementFrontend = createFrontend<PlacementProps>(
	(Messages) =>
		({ isContentRestricted }) => {
			const [message, setMessage] = useState<EligibleMessage<MessageTemplateContext>>();
			const { data, error, isLoading } = usePlacementApi<{
				messages: Array<EligibleMessage<MessageTemplateContext>>;
			}>('/');

			useEffect(() => {
				if (!data?.messages.length || error || isLoading || !isContentRestricted) {
					return;
				}

				const cohort = FeatureGates.getExperimentValue<string>(
					'share_and_perms_e2e_feature_gate_aa',
					'cohort',
					'not-enrolled',
					{ fireExperimentExposure: true },
				);

				if (cohort === 'variation') {
					setMessage(data.messages[0]);
				} else {
					// Reset the message just in case experiment value changes
					setMessage(undefined);
				}
			}, [data?.messages, error, isLoading, isContentRestricted]);

			const handleDismiss = () => {
				setMessage(undefined);
			};

			if (error) {
				throw error;
			}

			if (isLoading || !message) {
				return null;
			}

			return <Messages.render {...message} onDismiss={handleDismiss} />;
		},
);

export const PlacementBackend = createBackend();
